import PropTypes from "prop-types";

// @mui
import { Avatar, Box, LinearProgress, Button, Stack } from "@mui/material";
// components
import {
  DataGrid,
  GridToolbarColumnsButton,
  GridToolbarContainer,
} from "@mui/x-data-grid";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { upperCase } from "lodash";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  resetFilterState,
  selectTableFilter,
  setFilters,
  setMainCondition,
  setTableName,
} from "../../redux/slices/tableFilter.slice";
import CustomNoRowsOverlay from "./CustomNoRowsOverlay";
import FilterForm from "../filter/CustomFilterForm";
import { UserListToolbar } from "../../sections/@dashboard/user";
import Label from "../label/Label";
import Iconify from "../iconify";


CustomTable.propTypes = {
  canSelect: PropTypes.bool,
  searchLabel: PropTypes.string,
  searchMaxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isLoading: PropTypes.bool,
  isFetching: PropTypes.bool,
  tableName: PropTypes.string,
  restrictDownloadCols: PropTypes.array,
  searchCols: PropTypes.array,
  defaultVisibleCols: PropTypes.array,
  extraHeaderField: PropTypes.element,
  resetParentForm: PropTypes.func,
  actions: PropTypes.object,
  useDataQuery: PropTypes.func,
  defaultQueryParams: PropTypes.object,
  dataQueryOptions: PropTypes.object,
  sx: PropTypes.object,
  showBulkDelete: PropTypes.bool,
  onBulkDelete: PropTypes.func,
  showBulkStopSubscription: PropTypes.bool,
  onBulkStopSubscription: PropTypes.func,
  isSupport: PropTypes.bool,
  columnsOverride: PropTypes.array,
};

CustomTable.defaultProps = {
  canSelect: false,
  searchLabel: "Search...",
  searchMaxWidth: "100%",
  isLoading: false,
  isFetching: false,
  tableName: "",
  restrictDownloadCols: [],
  searchCols: [],
  defaultVisibleCols: [],
  extraHeaderField: null,
  resetParentForm: null,
  actions: null,
  useDataQuery: null,
  defaultQueryParams: {},
  dataQueryOptions: {},
  sx: {},
  showBulkDelete: false,
  onBulkDelete: null,
  showBulkStopSubscription: false,
  onBulkStopSubscription: null,
  isSupport: false,
  columnsOverride: null,
};

const CustomToolbar = ({
  selectedRows = [],
  showBulkDelete = false,
  onBulkDelete = null,
  showBulkStopSubscription = false,
  onBulkStopSubscription = null,
}) => (
  <GridToolbarContainer
    sx={{
      display: "flex",
      justifyContent: "space-between",
      padding: "10px 16px",
      borderRadius: 0,
    }}
  >
    <GridToolbarColumnsButton />
    <Stack direction="row" spacing={1}>
      {showBulkDelete && selectedRows.length > 0 && (
        <Button
          variant="contained"
          color="error"
          size="small"
          startIcon={<Iconify icon="eva:trash-2-fill" />}
          onClick={() => {
            if (onBulkDelete) {
              onBulkDelete(selectedRows);
            }
          }}
        >
          Delete Selected ({selectedRows.length})
        </Button>
      )}
      {showBulkStopSubscription && selectedRows.length > 0 && (
        <Button
          variant="contained"
          color="error"
          size="small"
          startIcon={<Iconify icon="eva:close-circle-fill" />}
          onClick={() => {
            if (onBulkStopSubscription) {
              onBulkStopSubscription(selectedRows);
            }
          }}
        >
          Stop Subscriptions ({selectedRows.length})
        </Button>
      )}
    </Stack>
  </GridToolbarContainer>
);

CustomToolbar.propTypes = {
  selectedRows: PropTypes.array,
  showBulkDelete: PropTypes.bool,
  onBulkDelete: PropTypes.func,
  showBulkStopSubscription: PropTypes.bool,
  onBulkStopSubscription: PropTypes.func,
};

const statusColor = {
  active: "success",
  inactive: "error",
  pending: "warning",
  deleted: "error",
  confirm: "success",
  cancelled: "error",
  success: "success",
  in_progress: "warning",
  "in process": "warning",
  "In Process": "warning",
  failed: "error",
  true: "success",
  false: "error",
  No: "error",
  Yes: "success",
  no: "error",
  yes: "success",
  approved: "success",
  rejected: "error",
  pending_registration: "warning",
};

export const CustomFormSchema = yup.object().shape({
  mainCondition: yup.string().required("Main condition is required"),
  filters: yup.array().of(
    yup.object().shape({
      field: yup.string().required("Field is required"),
      operator: yup.string().required("Operator is required"),
    }),
  ),
});

const applyFilterRules = (records, queryFilters) => {
  if (!records || !records.length || !queryFilters || !queryFilters.filters || !queryFilters.filters.length) {
    return records || [];
  }
  const filters = queryFilters.filters;
  const mainCondition = (queryFilters.mainCondition || "and").toLowerCase();

  return records.filter((row) => {
    const matchResults = filters.map((f) => {
      const field = f.field;
      const op = f.operator || f.condition;
      const value = f.value;

      if (!field || !op) return true;

      // Resolve row value supporting casing or nested structure
      let rowVal = row[field];
      if (rowVal === undefined && typeof field === "string") {
        rowVal = row[field.toLowerCase()] ?? row[field.charAt(0).toLowerCase() + field.slice(1)];
      }

      const strRowVal = rowVal !== null && rowVal !== undefined ? String(rowVal).toLowerCase().trim() : "";
      const strFilterVal =
        value !== null && value !== undefined && typeof value === "string"
          ? value.toLowerCase().trim()
          : value !== null && value !== undefined
          ? String(value).toLowerCase().trim()
          : "";

      switch (op) {
        case "startsWith":
          return strRowVal.startsWith(strFilterVal);
        case "endsWith":
          return strRowVal.endsWith(strFilterVal);
        case "contains":
          return strRowVal.includes(strFilterVal);
        case "notContains":
          return !strRowVal.includes(strFilterVal);
        case "equals":
        case "is":
          return strRowVal === strFilterVal;
        case "notEquals":
        case "not":
          return strRowVal !== strFilterVal;
        case "isEmpty":
          return strRowVal === "" || rowVal === null || rowVal === undefined;
        case "isNotEmpty":
          return strRowVal !== "" && rowVal !== null && rowVal !== undefined;
        case "isAnyOf":
          if (Array.isArray(value)) {
            return value.some((v) => String(v).toLowerCase().trim() === strRowVal);
          }
          return strRowVal.includes(strFilterVal);
        case "true":
          return rowVal === true || strRowVal === "true" || strRowVal === "yes";
        case "false":
          return rowVal === false || strRowVal === "false" || strRowVal === "no";
        case "greaterThan":
          return Number(rowVal) > Number(value);
        case "lessThan":
          return Number(rowVal) < Number(value);
        case "between":
          if (Array.isArray(value) && value.length >= 2) {
            return Number(rowVal) >= Number(value[0]) && Number(rowVal) <= Number(value[1]);
          }
          return true;
        case "exact": {
          if (!rowVal || !value) return false;
          const rDate = new Date(rowVal).toISOString().split("T")[0];
          const fDate = new Date(value).toISOString().split("T")[0];
          return rDate === fDate;
        }
        case "notExact": {
          if (!rowVal || !value) return true;
          const rDate = new Date(rowVal).toISOString().split("T")[0];
          const fDate = new Date(value).toISOString().split("T")[0];
          return rDate !== fDate;
        }
        case "after": {
          if (!rowVal || !value) return false;
          return new Date(rowVal).getTime() > new Date(value).getTime();
        }
        case "onOrAfter": {
          if (!rowVal || !value) return false;
          return new Date(rowVal).getTime() >= new Date(value).getTime();
        }
        case "before": {
          if (!rowVal || !value) return false;
          return new Date(rowVal).getTime() < new Date(value).getTime();
        }
        case "onOrBefore": {
          if (!rowVal || !value) return false;
          return new Date(rowVal).getTime() <= new Date(value).getTime();
        }
        case "betweenDate": {
          if (!rowVal || !Array.isArray(value) || value.length < 2) return true;
          const t = new Date(rowVal).getTime();
          return t >= new Date(value[0]).getTime() && t <= new Date(value[1]).getTime();
        }
        default:
          return true;
      }
    });

    if (mainCondition === "or") {
      return matchResults.some(Boolean);
    }
    return matchResults.every(Boolean);
  });
};

export default function CustomTable({
  canSelect,
  searchLabel,
  searchMaxWidth,
  tableName,
  extraHeaderField,
  resetParentForm,
  useDataQuery,
  defaultQueryParams,
  dataQueryOptions,
  actions,
  sx,
  showBulkDelete,
  onBulkDelete,
  showBulkStopSubscription,
  onBulkStopSubscription,
  isSupport,
  columnsOverride,
  searchCols = [],
  ...props
}) {
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const dispatch = useDispatch();



  const [customColumns, setCustomColumns] = useState([]);

  const [openFilterForm, setOpenFilterForm] = useState(false);

  const [selected, setSelected] = useState([]);

  const [queryFilters, setQueryFilters] = useState({});

  const downloadRef = useRef(null);

  const [filterName, setFilterName] = useState("");

  const [sortModel, setSortModel] = useState({});

  const [selectAll, setSelectAll] = useState(false);

  const defaultVisibilityModelState = useCallback(
    (providedCols) =>
      providedCols?.reduce((acc, column) => {
        if (canSelect) {
          acc.__check__ = true;
        }
        acc[column.field] = column?.defaultVisible;
        if (column?.field === "actions") {
          acc[column.field] = true;
        }
        return acc;
      }, {}),
    [canSelect],
  );

  const [columnVisibilityModel, setColumnVisibilityModel] = useState(
    defaultVisibilityModelState(customColumns),
  );

  const [submittedFilterForm, setSubmittedFilterForm] = useState({});

  const queryFilterString = useMemo(() => {
    if (queryFilters?.filters?.length > 0) {
      return JSON.stringify(queryFilters);
    }
    return undefined;
  }, [queryFilters]);

  const {
    data: fetchedData,
    isFetching: isDataFetching,
    isLoading: isDataLoading,
  } = useDataQuery(
    {
      skip: paginationModel.page * paginationModel.pageSize,
      limit: paginationModel.pageSize,
      search: filterName || undefined,
      filter: queryFilterString,
      sortBy: sortModel?.field,
      orderBy: sortModel?.sort,
      ...defaultQueryParams,
    },
    {
      ...dataQueryOptions,
    },
  );

  const filteredRows = useMemo(() => {
    let rows = fetchedData?.data ? [...fetchedData.data] : [];

    // 1. Search Filter (filterName)
    if (filterName && typeof filterName === "string" && filterName.trim()) {
      const q = filterName.trim().toLowerCase();
      const colsToSearch =
        searchCols && searchCols.length > 0
          ? searchCols
          : customColumns?.map((c) => c.field).filter((f) => f && f !== "actions");

      rows = rows.filter((row) => {
        if (!colsToSearch || colsToSearch.length === 0) {
          return Object.values(row).some((val) =>
            val !== null && val !== undefined && String(val).toLowerCase().includes(q)
          );
        }
        return colsToSearch.some((colField) => {
          const val = row[colField];
          if (val === null || val === undefined) return false;
          if (typeof val === "object") {
            return JSON.stringify(val).toLowerCase().includes(q);
          }
          return String(val).toLowerCase().includes(q);
        });
      });
    }

    // 2. Drawer Query Filters (queryFilters)
    if (queryFilters?.filters && queryFilters.filters.length > 0) {
      rows = applyFilterRules(rows, queryFilters);
    }

    // 3. Parent / External Filter Params (defaultQueryParams)
    if (defaultQueryParams && typeof defaultQueryParams === "object") {
      const keys = Object.keys(defaultQueryParams).filter(
        (k) =>
          defaultQueryParams[k] !== undefined &&
          defaultQueryParams[k] !== "" &&
          defaultQueryParams[k] !== null
      );
      if (keys.length > 0) {
        rows = rows.filter((row) => {
          return keys.every((key) => {
            const targetVal = defaultQueryParams[key];
            if (targetVal === undefined || targetVal === "" || targetVal === null) return true;

            // Direct field match
            if (row[key] !== undefined) {
              if (Array.isArray(row[key])) {
                return row[key].some((item) =>
                  typeof item === "object"
                    ? item?.id === targetVal ||
                      item?._id === targetVal ||
                      item?.name === targetVal ||
                      item?.email === targetVal
                    : item === targetVal
                );
              }
              return String(row[key]) === String(targetVal);
            }

            // Key-specific fallback checks:
            // graphId / sectionId
            if (key === "graphId" || key === "sectionId") {
              const gId =
                row?.graphId ||
                row?.graphSection?.id ||
                row?.graphSection?._id ||
                row?.graphSectionId;
              if (gId) return String(gId) === String(targetVal);
            }
            // roleId
            if (key === "roleId") {
              if (Array.isArray(row.roleIds)) return row.roleIds.includes(targetVal);
              if (Array.isArray(row.roles)) {
                return row.roles.some((r) =>
                  typeof r === "object"
                    ? r?.id === targetVal || r?._id === targetVal
                    : r === targetVal
                );
              }
            }
            // authUserId
            if (key === "authUserId") {
              if (Array.isArray(row.authUserIds)) return row.authUserIds.includes(targetVal);
              if (Array.isArray(row.authUsers)) {
                return row.authUsers.some((u) =>
                  typeof u === "object"
                    ? u?.id === targetVal || u?._id === targetVal
                    : u === targetVal
                );
              }
            }

            return true;
          });
        });
      }
    }

    return rows;
  }, [
    fetchedData?.data,
    filterName,
    searchCols,
    customColumns,
    queryFilters,
    defaultQueryParams,
  ]);

  const rowCountRef = useRef(fetchedData?.totalCount || 0);

  const hasActiveFilters = useMemo(() => {
    const hasQueryFilter = queryFilters?.filters?.length > 0;
    const hasSearch = Boolean(filterName && filterName.trim());
    const hasDefaultParams =
      defaultQueryParams &&
      typeof defaultQueryParams === "object" &&
      Object.values(defaultQueryParams).some((v) => v !== undefined && v !== "" && v !== null);
    return hasQueryFilter || hasSearch || hasDefaultParams;
  }, [queryFilters, filterName, defaultQueryParams]);

  const rowCount = useMemo(() => {
    if (hasActiveFilters) {
      return filteredRows.length;
    }
    if (fetchedData?.totalCount !== undefined) {
      rowCountRef.current = fetchedData?.totalCount;
    }
    return rowCountRef.current;
  }, [fetchedData?.totalCount, hasActiveFilters, filteredRows.length]);

  // setting custom columns on data change
  useEffect(() => {
    // If columnsOverride is provided, use it directly (for APIs that don't supply column metadata)
    if (columnsOverride && columnsOverride.length && !customColumns?.length) {
      setCustomColumns(() => {
        const columns = [...columnsOverride];



        if (actions) {
          columns.push(actions);
        }
        return columns;
      });
      return;
    }

    if ((fetchedData?.columns?.length || fetchedData?.data?.length) && !customColumns?.length) {
      setCustomColumns(() => {
        let uniqueColumns = [];

        if (fetchedData?.columns && Array.isArray(fetchedData.columns) && fetchedData.columns.length > 0) {
          // Remove duplicate columns based on columnName to prevent React key errors
          uniqueColumns =
            fetchedData.columns.filter((column, index, self) => {
              const firstIndex = self.findIndex(
                (c) => c.columnName === column.columnName,
              );
              if (firstIndex !== index) {
                console.warn(
                  `Duplicate column found: ${column.columnName}. Removing duplicate entry.`,
                );
              }
              return firstIndex === index;
            });
        } else if (fetchedData?.data && Array.isArray(fetchedData.data) && fetchedData.data.length > 0) {
          // Auto-infer column metadata from first row if backend doesn't supply a columns array
          const firstRow = fetchedData.data[0];
          const excludedKeys = new Set(["id", "_id", "updatedAt", "deletedAt", "password", "hash", "__v"]);
          uniqueColumns = Object.keys(firstRow)
            .filter((k) => !excludedKeys.has(k) && typeof firstRow[k] !== "object")
            .map((k) => ({
              columnName: k,
              headerName: k
                .replace(/([A-Z])/g, " $1")
                .replace(/^./, (str) => str.toUpperCase())
                .trim(),
              type:
                typeof firstRow[k] === "number"
                  ? "number"
                  : typeof firstRow[k] === "boolean"
                  ? "boolean"
                  : k.toLowerCase().includes("date")
                  ? "date"
                  : "string",
              isSortable: true,
              isFilterable: true,
              defaultVisible: true,
              isDownloadable: true,
            }));
        }

        const columns = uniqueColumns
          ?.map((column) => {
            const defaultObject = {
              field: column?.columnName,
              headerName: column?.headerName,
              type: column?.type,
              minWidth: 150,
              sortable: column?.isSortable !== false,
              filterable: column?.isFilterable !== false,
              defaultVisible: column?.defaultVisible,
              isDownloadable: column?.isDownloadable,
            };
            switch (column?.type) {
              case "date":
                return {
                  ...defaultObject,
                  valueFormatter: (value) => {
                    if (!value) return "--";
                    // Extract date part directly from string to avoid timezone conversion
                    if (typeof value === "string" && value.includes("T")) {
                      const datePart = value.split("T")[0];
                      const parsed = moment(datePart, "YYYY-MM-DD", true);
                      return parsed.isValid()
                        ? parsed.format("DD/MM/YYYY")
                        : "--";
                    }
                    // Fallback for Date objects or other formats
                    const parsed = moment(value);
                    return parsed.isValid()
                      ? parsed.format("DD/MM/YYYY")
                      : "--";
                  },
                  valueGetter: (value) => (value ? new Date(value) : null),
                };
              case "boolean":
                return {
                  ...defaultObject,
                  valueFormatter: (value) => (value ? "Yes" : "No"),
                  renderCell: (params) => (
                    <Label color={statusColor[params.formattedValue]}>
                      {params.formattedValue}
                    </Label>
                  ),
                };
              case "enum":
                return {
                  ...defaultObject,
                  renderCell: (params) => (
                    <Label color={statusColor[params.formattedValue]}>
                      {params.formattedValue}
                    </Label>
                  ),
                };
              case "object":
                return null;
              case "image":
                return {
                  ...defaultObject,
                  renderCell: (params) => (
                    <Avatar
                      src={params.formattedValue}
                      alt={params?.row?.name}
                      slotProps={{
                        img: {
                          sx: {
                            objectFit: "contain",
                          },
                        },
                      }}
                      sx={{
                        width: 40,
                        height: 40,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        borderRadius: 1,
                        m: 1,
                      }}
                    />
                  ),
                };
              default:
                return {
                  ...defaultObject,
                  align: "left",
                  headerAlign: "left",
                  valueGetter: (params) => {
                    if (!params && params !== 0) {
                      return "--";
                    }
                    return params;
                  },
                };
            }
          })
          .filter((column) => column !== null);



        if (actions) {
          columns?.push(actions);
        }
        return columns ?? [];
      });
    }
  }, [fetchedData, actions, isSupport, tableName, columnsOverride]);

  // setting visibility model on column change
  useEffect(() => {
    setColumnVisibilityModel(defaultVisibilityModelState(customColumns));
  }, [customColumns]);

  const {
    control,
    register,
    watch,
    setValue,
    getValues,
    reset,
    handleSubmit,
    trigger,
    formState: { errors },
  } = useForm({
    defaultValues: {
      mainCondition: "and",
      filters: [],
    },
    resolver: yupResolver(CustomFormSchema),
  });

  const { fields, append, remove } = useFieldArray({
    control,
    shouldUnregister: true,
    name: "filters",
  });

  const tableFilter = useSelector(selectTableFilter);

  // setting submitted filter form on filter change
  useEffect(() => {
    if (
      tableFilter.tableName &&
      tableFilter.tableName === tableName &&
      tableFilter.filters.length > 0
    ) {
      setValue("mainCondition", tableFilter.mainCondition);
      setValue("filters", tableFilter.filters);
      setSubmittedFilterForm({
        mainCondition: tableFilter.mainCondition,
        filters: tableFilter.filters,
      });
      setQueryFilters({
        mainCondition: tableFilter.mainCondition,
        filters: tableFilter.filters,
      });
    } else if (tableFilter.tableName && tableFilter.tableName !== tableName) {
      setValue("mainCondition", "and");
      setValue("filters", []);
      setSubmittedFilterForm({
        mainCondition: "and",
        filters: [],
      });
      setQueryFilters({});
    }
  }, [tableFilter, tableName, setValue]);

  const activeFilterCount = useMemo(() => {
    if (tableFilter?.tableName && tableFilter.tableName === tableName) {
      return tableFilter.filters?.length || 0;
    }
    return 0;
  }, [tableFilter, tableName]);

  const serializedDefaultQueryParams = JSON.stringify(defaultQueryParams);
  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [serializedDefaultQueryParams]);

  const onFilterFormSubmit = () => {
    const formData = getValues();
    setQueryFilters({
      mainCondition: formData.mainCondition,
      filters: formData.filters,
    });
    setSubmittedFilterForm({
      mainCondition: formData.mainCondition,
      filters: formData.filters,
    });
    dispatch(setTableName(tableName));
    dispatch(setFilters(formData.filters));
    dispatch(setMainCondition(formData.mainCondition));
    setOpenFilterForm(false);
    setValue("mainCondition", formData?.mainCondition);
    setValue("filters", formData?.filters);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleCloseFilterForm = () => {
    setOpenFilterForm(false);
    setValue("mainCondition", submittedFilterForm?.mainCondition ?? "and");
    setValue("filters", submittedFilterForm?.filters);
  };

  const handleResetFilterForm = () => {
    getValues("filters")?.forEach((_, index) => remove(index));
    reset({
      mainCondition: "and",
      filters: [],
    });
    // setOpenFilterForm(false)
    setSubmittedFilterForm({
      mainCondition: "and",
      filters: [],
    });
    setQueryFilters({});
    setOpenFilterForm(false);
    dispatch(resetFilterState());
    if (resetParentForm) {
      resetParentForm();
    }
    setFilterName("");
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleSelectionChange = (ids) => {
    // If selectAll is checked and user starts selecting individual rows, uncheck selectAll
    if (selectAll) {
      setSelectAll(false);
    }

    setSelected((prev) => {
      const prevSelected = prev?.filter((row) => ids?.includes(row?.id));

      const selected = [...prevSelected];

      const newSelected = fetchedData?.data?.filter((row) =>
        ids?.includes(row?.id),
      );

      if (newSelected?.length) {
        selected.push(...newSelected);
      }

      return selected.filter(
        (value, index, self) =>
          self.findIndex((t) => t.id === value.id) === index,
      );
    });
  };

  const handleFilterByName = (filter) => {
    setFilterName(filter);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleColumnVisibilityModelChange = (model) => {
    if (Object.keys(model).length === 0) {
      setColumnVisibilityModel(
        customColumns.reduce((acc, column) => {
          acc[column.field] = true;
          return acc;
        }, {}),
      );
    } else {
      const newModel = customColumns.reduce((acc, column) => {
        acc[column.field] = model[column.field];
        return acc;
      }, {});
      setColumnVisibilityModel(newModel);
    }
  };

  const generateCSVContent = (dataRows) => {
    const data = dataRows.map((row) => {
      const str = Object.keys(columnVisibilityModel)
        .filter(
          (key) =>
            customColumns.find((column) => column.field === key)
              ?.isDownloadable &&
            key !== "__check__" &&
            key !== "actions" &&
            columnVisibilityModel[key],
        )
        .map((key) => {

          switch (customColumns.find((column) => column.field === key)?.type) {
            case "date":
              return row[key] ? moment(row[key]).format("DD/MM/YYYY") : "N/A";
            case "boolean":
              return row[key] ? "Yes" : "No";
            default:
              // Escape commas and quotes in string values
              if (typeof row[key] === "string") {
                const escaped = row[key].replace(/"/g, '""');
                return row[key].includes(",") || row[key].includes('"')
                  ? `"${escaped}"`
                  : row[key];
              }
              return row[key];
          }
        });
      return str.join(",");
    });
    const headers = Object.keys(columnVisibilityModel)
      .filter(
        (key) =>
          customColumns.find((column) => column.field === key)
            ?.isDownloadable &&
          key !== "__check__" &&
          key !== "actions" &&
          columnVisibilityModel[key],
      )
      .map(
        (key) =>
          customColumns.find((column) => column.field === key)?.headerName,
      );

    data.unshift(headers.join(","));
    return data.join("\n");
  };

  const handleDownload = () => {
    if (selectAll) {
      // Download all data - use current filtered rows with total count
      // The data is already fetched, we just need to download what's available
      // For full download, user should use the API with high limit
      const textContent = generateCSVContent(filteredRows);
      const blob = new Blob([textContent], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      downloadRef.current.href = url;
      downloadRef.current.click();
      URL.revokeObjectURL(url);
    } else {
      // Download selected rows only
      const textContent = generateCSVContent(selected);
      const blob = new Blob([textContent], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      downloadRef.current.href = url;
      downloadRef.current.click();
      URL.revokeObjectURL(url);
    }
  };

  const handleSelectAllChange = (event) => {
    setSelectAll(event.target.checked);
    if (event.target.checked) {
      // Clear individual selections when "Select All" is checked
      setSelected([]);
    }
  };

  const handlePaginationModelChange = (model) => {
    setPaginationModel(model);
  };

  const handleSortModelChange = (model) => {
    setSortModel({
      field: model[0]?.field,
      sort: upperCase(model[0]?.sort),
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        padding: 0,
      }}
    >
      <FilterForm
        open={openFilterForm}
        columns={customColumns?.filter((column) => column.field !== "actions" && column.filterable !== false)}
        onClose={handleCloseFilterForm}
        control={control}
        register={register}
        watch={watch}
        fields={fields}
        append={append}
        remove={remove}
        onSubmit={handleSubmit(onFilterFormSubmit)}
        setValue={setValue}
        handleSubmit={handleSubmit}
        trigger={trigger}
        errors={errors}
        prevFormData={submittedFilterForm}
        resetForm={handleResetFilterForm}
        tableName={tableName}
      />

      <UserListToolbar
        icon="download"
        numSelected={selectAll ? rowCount : selected.length}
        filterName={filterName}
        onFilterName={handleFilterByName}
        onActionClick={handleDownload}
        selected={selected}
        placeholder={searchLabel}
        searchMaxWidth={searchMaxWidth}
        onOpenFilter={() => setOpenFilterForm(true)}
        extraHeaderField={extraHeaderField}
        resetForm={handleResetFilterForm}
        totalFilterCount={activeFilterCount}
        selectAll={selectAll}
        onSelectAllChange={handleSelectAllChange}
        totalCount={rowCount}
      />

      <a
        ref={downloadRef}
        style={{ display: "none" }}
        download={`${tableName}_${new Date().toISOString().split("T")[0]}.csv`}
        hidden
      >
        a
      </a>

      {filteredRows?.every((row) => row?.id) && (
        <DataGrid
          columns={customColumns}
          rows={customColumns?.length ? filteredRows : []}
          // rows={fetchedData?.data || []}
          loading={isDataFetching || isDataLoading}
          getRowId={(row) => row?.id}
          // select
          checkboxSelection={canSelect}
          rowSelectionModel={selected.map((row) => row.id)}
          onRowSelectionModelChange={handleSelectionChange}
          disableRowSelectionOnClick
          keepNonExistentRowsSelected
          // onFilterModelChange={handleFilterModelChange}
          filterMode="client"
          // pagination
          pagination
          pageSizeOptions={[10, 25, 50, 100]}
          paginationModel={paginationModel}
          paginationMode="server"
          rowCount={rowCount}
          onPaginationModelChange={handlePaginationModelChange}
          // sorting
          sortingMode="server"
          onSortModelChange={handleSortModelChange}
          scrollbarSize={1}
          disableColumnMenu
          slots={{
            loadingOverlay: LinearProgress,
            noRowsOverlay: CustomNoRowsOverlay,
            toolbar: CustomToolbar,
          }}
          slotProps={{
            toolbar: {
              selectedRows: selected,
              showBulkDelete,
              onBulkDelete,
              showBulkStopSubscription,
              onBulkStopSubscription,
            },
            pagination: {
              showFirstButton: true,
              showLastButton: true,
              slotProps: {
                actions: {
                  nextButton: {
                    "data-testid": "pagination-next",
                  },
                  previousButton: {
                    "data-testid": "pagination-previous",
                  },
                },
                select: {
                  inputProps: {
                    "data-testid": "pagination-select-input",
                  },
                  "data-testid": "pagination-select",
                },
              },
            },
          }}
          onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
          columnVisibilityModel={columnVisibilityModel}
          // autosizeOptions={{
          //   columns: ["firstName", "lastName", "Id"],
          //   includeOutliers: true,
          //   includeHeaders: true,
          //   outliersFactor: 0.5,
          //   // expand: true,
          // }}

          sx={{
            flex: 1,
            width: "100%",
            "& .MuiDataGrid-scrollbar--horizontal": {
              // height: "5px!important",
              position: "relative",
              // "& .MuiDataGrid-scrollbarContent": {
              overflowX: "auto",
              "&::-webkit-scrollbar": {
                height: "10px",
              },
              "&::-webkit-scrollbar-track": {
                boxShadow: "inset 0 0 6px rgba(0,0,0,0.00)",
                webkitBoxShadow: "inset 0 0 6px rgba(0,0,0,0.00)",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "gray",
                opacity: 0,
                borderRadius: 5,
                "&:active": {
                  transform: "scale(0.8)",
                  opacity: 1,
                },
              },
              // },
            },
            "& .MuiDataGrid-filler": {
              height: "0px!important",
            },
            "& .MuiDataGrid-overlayWrapper": {
              height:
                isDataLoading || isDataFetching
                  ? "2px!important"
                  : "300px!important",
              transition: "height 0s !important",
              "& .MuiDataGrid-overlayWrapperInner": {
                height:
                  isDataLoading || isDataFetching
                    ? "2px!important"
                    : "300px!important",
                transition: "height 0s !important",
              },
            },
            borderRadius: 0,
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#F4F6F8",
              color: "#637381",
              borderRadius: 0,
            },
            ...sx,
          }}
          data-testid="custom-table"
          {...props}
        />
      )}
    </Box>
  );
}